import { useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { checkPromoCodeAndReferral, getDiscountsFromURL } from '@/utils/handleDiscount';
import config from '@/core/config/config';
import { GET_DISCOUNT_DETAILS, GET_PROMO_CODE_FROM_SESSION } from '@/graphql/queries';
import request from 'graphql-request';
import { useCookies } from 'react-cookie';
import { Fetcher } from 'swr';
import useSWRImmutable from 'swr/immutable';
import { useReferralCodeQuery } from './useReferralCode';

// TODO: move fetchers to a centralized location
const fetcher: Fetcher<{ promoCode: Location }, [string, any]> = ([query, variables]) => {
  return request(config.APPS_SERVER, query, variables);
};

const useGetPromoCodeFromSession = () => {
  const { data } = useSWRImmutable([GET_PROMO_CODE_FROM_SESSION], { fetcher });
  return {
    promoCodeFromSession: data?.getPromoCodeFromSession.promoCode,
    discountDetailsFromSession: data?.getPromoCodeFromSession.discountDetails,
  };
};

const useGetDiscountDetails = () => {
  const [cookies, setCookie] = useCookies();

  let promoCode = getDiscountsFromURL();

  if (promoCode && cookies?.discount !== promoCode) {
    // Set cookie for 14 days
    const cookieOptions = {
      expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 14),
      path: '/',
    };
    setCookie('discount', promoCode, cookieOptions);
  }

  if (!promoCode && cookies.discount) promoCode = cookies.discount;

  const query = promoCode ? GET_DISCOUNT_DETAILS : null;
  const variables = { promoCode, external: true };

  const { data } = useSWRImmutable([query, variables], { fetcher });

  return { discountDetails: data?.discountDetails };
};

export const useDiscount = () => {
  const { discountDetails } = useGetDiscountDetails();
  const { discountDetailsFromSession } = useGetPromoCodeFromSession();
  const discount = discountDetails || discountDetailsFromSession;

  // useReferralCodeSetInCookie();
  const { data: marketingReferral } = useReferralCodeQuery();

  const { addToPaymentSlice, discountType, locationId, setErrorModal, skuId } = usePrenuvoStore(
    useShallow((state) => ({
      addToPaymentSlice: state.payment.methods.add,
      discountType: state.payment.discountType,
      locationId: state.scan.location?.code,
      setErrorModal: state.ui.methods.setErrorModal,
      skuId: state.scan.sku?.skuID,
    })),
  );

  const setPromoCode = useCallback(() => {
    if (!discount || discountType) return;
    addToPaymentSlice({ discount, discountType: 'promo' });
  }, [addToPaymentSlice, discount, discountType]);

  const setReferralCode = useCallback(() => {
    if (!marketingReferral || discountType) return;
    addToPaymentSlice({ marketingReferral: marketingReferral, discountType: 'referral' });
  }, [addToPaymentSlice, discountType, marketingReferral]);

  useEffect(() => {
    const {
      discount: validDiscount,
      marketingReferral: validMarketingReferral,
      errorMessage,
    } = checkPromoCodeAndReferral({ discount, marketingReferral, locationId, skuId });
    if (validDiscount) {
      setPromoCode();
    }
    if (validMarketingReferral) {
      setReferralCode();
    }
    if (!validDiscount && !validMarketingReferral && discountType) {
      addToPaymentSlice({ discountType: '' });
    }
    if (!validMarketingReferral && errorMessage) {
      setErrorModal('', errorMessage);
    }
  }, [
    discount,
    marketingReferral,
    setPromoCode,
    setReferralCode,
    locationId,
    skuId,
    discountType,
    addToPaymentSlice,
    setErrorModal,
  ]);
};

export default useDiscount;
